import { ApiError } from "./apiClient";

type ValidationErrors = Record<string, string[]>;

function isValidationErrors(body: unknown): body is ValidationErrors {
  if (!body || typeof body !== "object" || Array.isArray(body)) return false;
  return Object.values(body).every(
    (messages) =>
      Array.isArray(messages) &&
      messages.every((message) => typeof message === "string"),
  );
}

// "user_id" -> "User", "password" -> "Password"; base errors are already
// complete sentences.
function formatMessage(attribute: string, message: string): string {
  if (attribute === "base") return message;
  const label = attribute.replace(/_id$/, "").replace(/_/g, " ");
  return `${label.charAt(0).toUpperCase()}${label.slice(1)} ${message}`;
}

/**
 * Converts a failed API request into user-facing messages. Rails validation
 * failures (422) arrive as `{ attribute: [messages] }` and are expanded into
 * full sentences; anything else falls back to `fallback`.
 */
export function getApiErrorMessages(
  error: unknown,
  fallback: string,
): string[] {
  if (!(error instanceof ApiError)) return [fallback];

  if (isValidationErrors(error.body)) {
    const messages = Object.entries(error.body).flatMap(
      ([attribute, attributeMessages]) =>
        attributeMessages.map((message) => formatMessage(attribute, message)),
    );
    if (messages.length > 0) return messages;
  }

  if (error.status === 403) {
    return ["You do not have permission to perform this action."];
  }

  return [fallback];
}
